import { useTranslation } from 'react-i18next';
import IDPList from '../../components/molecules/IDPList';
import { useTenant } from '../../providers/tenant';
import { useAzureMsal } from '../../providers/azure';
import { useEffect } from 'react';
import {
    AppInsights,
    Head,
    AccountManager,
    Util,
} from '@bluedolphin/util-module-mf';
import { Link, Typography, BluedolphinLogo, useTheme } from '@bluedolphin/ui';
import { CustomDivider } from '../../components/atoms/Divider';
import { CustomCard } from '../../components/atoms/Card';

const { useAppInsights } = AppInsights;

/**
 * Login page component
 *
 * @returns {JSX.Element}
 */
export default function Login() {
    const theme = useTheme();
    const { t } = useTranslation();
    const { tenantMeta: tenant } = useTenant();
    const appInstights = useAppInsights();
    const { clearSessionTimeout, login } = useAzureMsal();
    const { rootURL, landingPage, token, logout } = AccountManager.getTenant();

    /**
     * Updates Document title and tracks page view information
     */
    useEffect(() => {
        Head.setTitle(t('tabTitle'));
        if (appInstights) {
            appInstights.trackPageView({ name: 'login' });
        }
    }, []);

    /**
     * if the user logs out then the session timeoutId is cleared.
     *
     * Else if there is no token but there is one idp then autologin is fired
     *
     */
    useEffect(() => {
        if (tenant) {
            if (logout) {
                clearSessionTimeout();
            } else if (
                (!token || Util.JWT.isExpired(token)) &&
                tenant.b2CPolicies.length === 1
            ) {
                login(tenant.b2CPolicies[0]);
            }
        }
    }, [tenant]);

    useEffect(() => {
        if (token && !Util.JWT.isExpired(token)) {
            window.history.pushState({}, '', `${rootURL}${landingPage}`);
        }
    });

    return (
        <div className="signup__wrapper">
            <BluedolphinLogo
                color={theme.palette.primary.main}
            ></BluedolphinLogo>
            <CustomCard
                elevation={1}
                sx={{
                    borderColor: theme.palette.primary.main,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        color: theme.palette.text.primary,
                        display: 'inline',
                    }}
                >
                    {t('signIn.title')}
                    {tenant?.friendlyName}
                </Typography>
                <CustomDivider />
                {tenant && tenant?.b2CPolicies && (
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        {t('signIn.description')}
                    </Typography>
                )}
                <IDPList />
                <CustomDivider />
                {tenant?.b2CVendor && (
                    <Link
                        className="vendor-link"
                        sx={{
                            padding: '8px 0px',
                            display: 'flex',
                            alignSelf: 'stretch',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            gap: '16px',
                        }}
                        underline="hover"
                        onClick={() => login(tenant.b2CVendor)}
                        id={tenant?.b2CVendor?.id}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.primary.main,
                                display: 'inline',
                            }}
                        >
                            {t('links.vbLogin')}
                        </Typography>
                    </Link>
                )}
            </CustomCard>
        </div>
    );
}
